// Supervisor view of recruiter Q&A tickets ("פניות העובדות").
// Deliberately separate from the recruiterQa module: no polling, no unread
// state, no read-acks -- supervisor reads never ack recruiter unread.
// Supervisor writes (instruction / approve / answer / cancel / close / reopen):
//   - gated on GET /config `supervisor_writes` (recruiterQa/supervisorWrites)
//   - request_uuid generated here and RETAINED while the payload is identical
//     (network retry); a changed payload, or a resubmit after
//     request_uuid_conflict, gets a fresh uuid
//   - 409 stale_turn/stale_ticket keeps the typed draft and refreshes the thread
//   - instruction (INSTRUCTION_SPEC (b)/(f)): private "הנחיה למערכת" targeting the
//     latest genuine recruiter question (expected_question_id); 409
//     latest_question_changed keeps the text and refreshes so the new question
//     is reviewed first. There is no public message to the recruiter any more.
// Jira decisions (JIRA_DECISION_SPEC) on the per-turn Jira card:
//   - summaries cached by full task id for 30s, concurrent reads deduplicated
//   - gated on GET /config `jira_writes` (recruiterQa/jiraWrites) + server allowed_actions
//   - one decision per question: the uuid is kept across timeouts and polls; a
//     changed payload (user edit / refreshed versions) or a stale/uuid conflict
//     gets a fresh uuid on the NEXT explicit submit — never an automatic retry;
//     so does a stored terminal failure (Rails replays it for the same uuid)
//   - 502 jira_unconfirmed: card stays locked; "בדוק שוב" re-polls the same uuid
//   - a pending/unconfirmed decision is kept in sessionStorage and resumed after reload
//   - 202 / timeout = pending confirmation: poll the status endpoint every
//     retry_after_s for ~60s, then wait for a manual "בדוק שוב"
import * as qaApi from '@/services/recruiterQaApi'
import { uuidv4 } from '@/store/modules/recruiterQa'
import { JIRA_COPY } from '@/utils/recruiterQaJira'
import { latestGenuineQuestion, reAnswerOf } from '@/utils/recruiterQaFormat'

export const ADMIN_TABS = ['open', 'closed', 'all']

export const ADMIN_ERROR_MESSAGES = {
  list: 'לא הצלחנו לטעון את הפניות כרגע. אפשר לנסות שוב בעוד רגע.',
  thread: 'לא הצלחנו לטעון את הפנייה כרגע. אפשר לנסות שוב בעוד רגע.',
  not_found: 'הפנייה לא נמצאה.',
  feature_off: 'הפיצ׳ר "דבר עם המערכת" כבוי כרגע.'
}

/** Append a page to the list, dropping ids already shown (cursor overlap / refresh races). */
export const mergeTickets = (existing, incoming) => {
  const seen = {}
  const out = []
  ;(existing || []).concat(incoming || []).forEach(t => {
    if (!t || seen[t.root_id]) return
    seen[t.root_id] = true
    out.push(t)
  })
  return out
}

const errorMessage = (err, mapError, fallback) => {
  const mapped = mapError(err)
  if (mapped.kind === 'forbidden') return mapped.message
  if (mapped.kind === 'feature_off') return ADMIN_ERROR_MESSAGES.feature_off
  if (mapped.kind === 'not_found') return ADMIN_ERROR_MESSAGES.not_found
  return fallback
}

export const ANSWERABLE_STATUSES = ['queued', 'running', 'consulting', 'needs_input', 'needs_human', 'failed']
export const CANCELLABLE_STATUSES = ['queued', 'running', 'consulting']

const isQuestionTurn = q => !!q && (q.kind || 'question') === 'question'

export const INSTRUCTION_ACCEPTED_TEXT = 'ההנחיה נשמרה. המערכת מכינה תשובה מעודכנת.'

/** Thread `execution` block ({latest_question_id, active_job_id}) or null (legacy server). */
const executionOf = thread => {
  const e = thread && thread.execution
  return (e && typeof e === 'object') ? e : null
}

/**
 * Target of the instruction composer: the server's execution.latest_question_id
 * (row from the thread when loaded, else {id}); legacy server: newest genuine question.
 */
export const instructionTarget = thread => {
  const rows = (thread && thread.questions) || []
  const e = executionOf(thread)
  if (!e) return latestGenuineQuestion(rows)
  const id = parseInt(e.latest_question_id, 10)
  if (!(id > 0)) return null
  return rows.filter(q => q && q.id === id)[0] || { id, question: '' }
}

/**
 * Per-turn supervisor actions address only the active execution of the latest
 * question (execution.active_job_id; null = superseded without replacement).
 * Legacy server (no execution block): every turn, as before.
 */
export const isActiveExecution = (thread, q) => {
  const e = executionOf(thread)
  if (!e) return true
  return !!q && e.active_job_id !== null && e.active_job_id !== undefined && q.id === parseInt(e.active_job_id, 10)
}

/** Row of the latest question (original or re-answer) displaced by a newer execution. */
export const isDisplacedExecution = (thread, q) => {
  const e = executionOf(thread)
  if (!e || !q) return false
  const latest = parseInt(e.latest_question_id, 10)
  if (!(latest > 0)) return false
  const belongs = q.id === latest || reAnswerOf(q) === latest
  return belongs && !isActiveExecution(thread, q)
}
export const canApprove = q => isQuestionTurn(q) && q.status === 'needs_human'
export const canAnswer = q => isQuestionTurn(q) && ANSWERABLE_STATUSES.indexOf(q.status) !== -1
export const canCancel = q => isQuestionTurn(q) && CANCELLABLE_STATUSES.indexOf(q.status) !== -1

// Operation keys (drafts / errors / busy / uuid retention), scoped per thread.
export const opKey = (rootId, action, id) => rootId + ':' + action + (id ? ':' + id : '')

export const JIRA_SUMMARY_TTL_MS = 30000
export const JIRA_STATUSES_BATCH = 50
/** Task ids linked to a page of tickets (uniq, order kept). */
export const jiraIdsOf = tickets => {
  const out = []
  ;(Array.isArray(tickets) ? tickets : []).forEach(t => {
    (t && Array.isArray(t.jira_task_ids) ? t.jira_task_ids : []).forEach(id => {
      const s = String(id || '')
      if (s && out.indexOf(s) === -1) out.push(s)
    })
  })
  return out
}
export const JIRA_POLL_WINDOW_MS = 60000

/** Draft key of the reject/edit text for one question's Jira card. */
export const jiraDraftKey = (rootId, action, questionId) => opKey(rootId, 'jira_' + action, questionId)

/** Jira-decision states in which the card's buttons are disabled. */
export const isJiraDecisionBusy = dec => !!dec && (dec.state === 'submitting' || dec.state === 'pending' || dec.state === 'unconfirmed')

export const JIRA_PENDING_STORAGE_KEY = 'rqa_jira_pending_v1'

const sessionStore = {
  get: key => { try { return window.sessionStorage.getItem(key) } catch (e) { return null } },
  set: (key, value) => { try { window.sessionStorage.setItem(key, value) } catch (e) {} },
  remove: key => { try { window.sessionStorage.removeItem(key) } catch (e) {} }
}

// Error kinds after which the open thread is refetched (draft always kept).
const REFRESH_KINDS = ['stale', 'transition_not_allowed', 'precondition', 'draft_missing', 'latest_question_changed', 'instruction_flow_required']

const emptyThread = () => ({ rootId: null, loading: false, error: '', ticket: null, questions: [], truncated: false, execution: null })

export const createRecruiterQaAdminModule = (overrides = {}) => {
  const timers = typeof window !== 'undefined' ? window : global
  const deps = {
    api: qaApi,
    mapError: qaApi.mapError,
    mapSupervisorError: qaApi.mapSupervisorError,
    mapJiraError: qaApi.mapJiraError,
    uuid: uuidv4,
    now: () => Date.now(),
    storage: sessionStore,
    setTimeout: (fn, ms) => timers.setTimeout(fn, ms),
    clearTimeout: id => timers.clearTimeout(id),
    ...overrides
  }
  // Non-reactive request sequence numbers: a stale response never overwrites newer state.
  // ops: opKey -> { uuid, digest, renew } — the idempotency key of the last
  // attempt; reused only for an identical payload.
  const rt = { listSeq: 0, threadSeq: 0, ops: {}, jiraSeq: {}, jiraInflight: {}, jiraOps: {}, jiraPolls: {} }

  const uuidFor = (key, payload) => {
    const digest = JSON.stringify(payload)
    let op = rt.ops[key]
    if (!op || op.renew || op.digest !== digest) {
      op = { uuid: deps.uuid(), digest, renew: false }
      rt.ops[key] = op
    }
    return op.uuid
  }

  // One write. `call(request_uuid)` performs the request; `payload` is what
  // identifies the operation (uuid retained while it is unchanged).
  const runOp = async (ctx, { rootId, key, payload, call, maxChars, draftKey, notice }) => {
    const { state, commit, dispatch } = ctx
    if (state.busy[key]) return { ok: false, busy: true }
    const uuid = uuidFor(key, payload)
    commit('OP_START', key)
    let data
    try {
      data = await call(uuid)
    } catch (e) {
      const err = deps.mapSupervisorError(e, { maxChars })
      if (err.kind === 'uuid_conflict' && rt.ops[key]) rt.ops[key].renew = true // fresh uuid on the NEXT user submit
      if (err.kind === 'feature_off' || err.kind === 'forbidden') commit('SET_WRITES_BLOCKED', true)
      commit('OP_FAILURE', { key, message: err.message })
      if (REFRESH_KINDS.indexOf(err.kind) !== -1) {
        if (err.current && state.thread.rootId === rootId) commit('THREAD_PATCH', err.current)
        if (state.thread.rootId === rootId) dispatch('openThread', rootId)
      }
      return { ok: false, error: err, message: err.message, requestUuid: uuid }
    }
    delete rt.ops[key]
    commit('OP_SUCCESS', key)
    if (notice) commit('SET_NOTICE', { key, text: notice })
    if (draftKey) commit('SET_DRAFT', { key: draftKey, text: '' })
    if (state.thread.rootId === rootId) {
      commit('THREAD_PATCH', { question: data && data.question, ticket: data && data.ticket })
      dispatch('openThread', rootId)
    }
    dispatch('loadTickets')
    return { ok: true, data, requestUuid: uuid }
  }

  const questionOf = (state, id) => (state.thread.questions || []).filter(q => q && q.id === id)[0] || null

  // ---- Jira decisions ----
  const jiraUuidFor = (questionId, payload) => {
    const digest = JSON.stringify(payload)
    let op = rt.jiraOps[questionId]
    if (!op || op.renew || op.digest !== digest) {
      op = { uuid: deps.uuid(), digest, renew: false }
      rt.jiraOps[questionId] = op
    }
    return op.uuid
  }

  // Persisted pending decisions: questionId -> { uuid, digest, action, rootId, taskId }.
  const readPersisted = () => {
    try {
      const parsed = JSON.parse(deps.storage.get(JIRA_PENDING_STORAGE_KEY) || '{}')
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (e) { return {} }
  }
  const persistPending = (questionId, dec) => {
    const all = readPersisted()
    const op = rt.jiraOps[questionId]
    all[questionId] = { uuid: dec.uuid, digest: op ? op.digest : null, action: dec.action, rootId: dec.rootId, taskId: dec.taskId }
    deps.storage.set(JIRA_PENDING_STORAGE_KEY, JSON.stringify(all))
  }
  const forgetPending = questionId => {
    const all = readPersisted()
    if (!all[questionId]) return
    delete all[questionId]
    if (Object.keys(all).length) deps.storage.set(JIRA_PENDING_STORAGE_KEY, JSON.stringify(all))
    else deps.storage.remove(JIRA_PENDING_STORAGE_KEY)
  }
  const markPending = (ctx, questionId, patch) => {
    ctx.commit('JIRA_DECISION_PATCH', { questionId, patch: { message: '', ...patch } })
    const dec = ctx.state.jiraDecisions[questionId]
    if (dec && dec.uuid) persistPending(questionId, dec)
  }

  const stopPoll = questionId => {
    const poll = rt.jiraPolls[questionId]
    if (poll && poll.timer) deps.clearTimeout(poll.timer)
    delete rt.jiraPolls[questionId]
  }

  // Next status check in `delayS`; past the ~60s window the card stays pending
  // and waits for a manual "בדוק שוב".
  const schedulePoll = (ctx, questionId, delayS) => {
    const prev = rt.jiraPolls[questionId]
    if (prev && prev.timer) deps.clearTimeout(prev.timer)
    const deadline = prev ? prev.deadline : deps.now() + JIRA_POLL_WINDOW_MS
    const delayMs = Math.max(1, Number(delayS) || 2) * 1000
    if (deps.now() + delayMs > deadline) {
      rt.jiraPolls[questionId] = { timer: null, deadline }
      ctx.commit('JIRA_DECISION_PATCH', { questionId, patch: { pollExpired: true } })
      return
    }
    rt.jiraPolls[questionId] = {
      deadline,
      timer: deps.setTimeout(() => {
        rt.jiraPolls[questionId] = { timer: null, deadline }
        ctx.dispatch('pollJiraDecision', { questionId })
      }, delayMs)
    }
  }

  const finishJiraDecision = (ctx, questionId, res) => {
    const { state, commit, dispatch } = ctx
    const dec = state.jiraDecisions[questionId] || {}
    stopPoll(questionId)
    delete rt.jiraOps[questionId]
    forgetPending(questionId)
    commit('JIRA_DECISION_SET', { questionId, decision: { ...dec, state: 'succeeded', message: '', stale: false, pollExpired: false } })
    if (dec.rootId && dec.action) commit('SET_DRAFT', { key: jiraDraftKey(dec.rootId, dec.action, questionId), text: '' })
    if (res && res.jira && dec.taskId) commit('JIRA_SUMMARY_SUCCESS', { taskId: dec.taskId, jira: res.jira, fetchedAt: deps.now() })
    if (dec.taskId) {
      dispatch('loadJiraSummary', { questionId, taskId: dec.taskId, force: true })
      dispatch('loadJiraStatuses', [dec.taskId])
    }
    if (dec.rootId && state.thread.rootId === dec.rootId) {
      commit('THREAD_PATCH', { question: res && res.question, ticket: res && res.ticket })
      dispatch('openThread', dec.rootId)
    }
    dispatch('loadTickets')
    return { ok: true, data: res, requestUuid: dec.uuid }
  }

  // `fromPoll`: a 404 on the status endpoint means the operation was never
  // accepted — safe to report "not sent" (the uuid is kept for a resend).
  const failJiraDecision = (ctx, questionId, err, { fromPoll } = {}) => {
    const { state, commit, dispatch } = ctx
    const dec = state.jiraDecisions[questionId] || {}
    // Maybe committed (timeout / unexpected 5xx), or a 401 while confirming: stay pending.
    if (err.uncertain || (fromPoll && err.kind === 'unauthorized')) {
      markPending(ctx, questionId, { state: 'pending' })
      if (!fromPoll) rt.jiraPolls[questionId] = null
      schedulePoll(ctx, questionId, 2)
      return { ok: false, pending: true, requestUuid: dec.uuid }
    }
    stopPoll(questionId)
    // Delivery not confirmed after many attempts: lock the card, same uuid for "בדוק שוב".
    if (err.kind === 'unconfirmed') {
      markPending(ctx, questionId, { state: 'unconfirmed', pollExpired: false })
      ctx.commit('JIRA_DECISION_PATCH', { questionId, patch: { message: err.message } })
      return { ok: false, unconfirmed: true, error: err, message: err.message, requestUuid: dec.uuid }
    }
    forgetPending(questionId)
    let message = err.message
    if (fromPoll && err.kind === 'not_found' && err.code !== 'jira_task_not_found') message = qaApi.JIRA_MESSAGES.not_sent
    const renew = err.terminal || err.kind === 'stale' || err.kind === 'uuid_conflict'
    if (renew && rt.jiraOps[questionId]) rt.jiraOps[questionId].renew = true
    if (err.kind === 'feature_off' || err.kind === 'forbidden') commit('SET_JIRA_WRITES_BLOCKED', true)
    commit('JIRA_DECISION_SET', {
      questionId,
      decision: { ...dec, state: 'failed', message, stale: err.kind === 'stale', pollExpired: false }
    })
    if (err.kind === 'stale' || err.kind === 'precondition') {
      if (err.current && dec.taskId) commit('JIRA_SUMMARY_SUCCESS', { taskId: dec.taskId, jira: err.current, fetchedAt: deps.now() })
      else if (dec.taskId) dispatch('loadJiraSummary', { questionId, taskId: dec.taskId, force: true })
      if (dec.rootId && state.thread.rootId === dec.rootId) dispatch('openThread', dec.rootId)
    }
    return { ok: false, error: err, message, requestUuid: dec.uuid }
  }

  const applyJiraResult = (ctx, questionId, res) => {
    if (res && res.state === 'pending') {
      markPending(ctx, questionId, { state: 'pending' })
      schedulePoll(ctx, questionId, res.retry_after_s)
      return { ok: false, pending: true, requestUuid: (ctx.state.jiraDecisions[questionId] || {}).uuid }
    }
    return finishJiraDecision(ctx, questionId, res)
  }

  return {
    namespaced: true,
    state: () => ({
      tickets: [],
      nextCursor: null,
      admins: [],
      filterState: 'open',
      filterAdminId: null,
      loading: false,
      loadingMore: false,
      loaded: false,
      error: '',
      thread: emptyThread(),
      drafts: {},
      busy: {},
      opErrors: {},
      // opKey -> success copy (instruction accepted); cleared on the next edit / attempt.
      opNotices: {},
      // Set after a write answered 404 feature_off / 403: hide write controls.
      writesBlocked: false,
      // Jira card: taskId -> { loading, error, data, fetchedAt }
      jiraSummaries: {},
      // taskId -> status object | null (null = asked, unknown right now). A
      // missing key = not asked yet. Filled after each list page, never awaited.
      jiraStatuses: {},
      // questionId -> { state: submitting|pending|succeeded|failed, action, uuid, rootId, taskId, message, stale, pollExpired }
      jiraDecisions: {},
      jiraWritesBlocked: false
    }),
    getters: {
      // Server-decided capability (GET /config supervisor_writes, owned by recruiterQa).
      canWrite: (state, getters, rootState, rootGetters) =>
        !state.writesBlocked && !!rootGetters && rootGetters['recruiterQa/supervisorWrites'] === true,
      // Jira decision buttons: supervisor writes AND GET /config jira_writes.
      canJiraWrite: (state, getters, rootState, rootGetters) =>
        getters.canWrite && !state.jiraWritesBlocked && rootGetters['recruiterQa/jiraWrites'] === true,
      hasMore: state => !!state.nextCursor,
      isEmpty: state => state.loaded && !state.loading && !state.error && state.tickets.length === 0
    },
    mutations: {
      SET_FILTERS (state, { filterState, filterAdminId }) {
        if (filterState !== undefined) state.filterState = ADMIN_TABS.indexOf(filterState) !== -1 ? filterState : 'open'
        if (filterAdminId !== undefined) {
          const id = parseInt(filterAdminId, 10)
          state.filterAdminId = id > 0 ? id : null
        }
      },
      // A non-append load (first page / filter / tab change) drops the old list and
      // cursor at once, so nothing from the previous filter can be paged onto it.
      LIST_START (state, { append }) {
        if (append) {
          state.loadingMore = true
        } else {
          state.loading = true
          state.loadingMore = false
          state.tickets = []
          state.nextCursor = null
        }
        state.error = ''
      },
      LIST_SUCCESS (state, { data, append }) {
        const incoming = Array.isArray(data && data.tickets) ? data.tickets : []
        state.tickets = append ? mergeTickets(state.tickets, incoming) : mergeTickets([], incoming)
        state.nextCursor = (data && data.next_cursor) || null
        if (Array.isArray(data && data.admins)) state.admins = data.admins
        state.loading = false
        state.loadingMore = false
        state.loaded = true
      },
      JIRA_STATUSES_MERGE (state, map) {
        state.jiraStatuses = { ...state.jiraStatuses, ...(map || {}) }
      },
      LIST_FAILURE (state, message) {
        state.loading = false
        state.loadingMore = false
        state.loaded = true
        state.error = message
      },
      THREAD_START (state, rootId) {
        const same = state.thread.rootId === rootId
        state.thread = { ...(same ? state.thread : emptyThread()), rootId, loading: true, error: '' }
      },
      THREAD_SUCCESS (state, data) {
        state.thread = {
          ...state.thread,
          loading: false,
          error: '',
          ticket: (data && data.ticket) || null,
          questions: Array.isArray(data && data.questions) ? data.questions : [],
          truncated: !!(data && data.truncated),
          execution: (data && data.execution && typeof data.execution === 'object') ? data.execution : null
        }
      },
      THREAD_FAILURE (state, message) {
        state.thread = { ...state.thread, loading: false, error: message }
      },
      THREAD_CLOSE (state) {
        state.thread = emptyThread()
      },
      // Merge a server-returned question row and/or root ticket state into the open thread.
      THREAD_PATCH (state, { question, ticket } = {}) {
        if (!state.thread.rootId) return
        const next = { ...state.thread }
        if (question && question.id) {
          const rows = (next.questions || []).slice()
          const i = rows.map(q => q.id).indexOf(question.id)
          if (i === -1) rows.push(question)
          else rows.splice(i, 1, { ...rows[i], ...question })
          next.questions = rows.sort((a, b) => a.id - b.id)
        }
        if (ticket && typeof ticket === 'object' && (!ticket.root_id || ticket.root_id === next.rootId)) {
          next.ticket = { ...(next.ticket || {}), ...ticket }
        }
        state.thread = next
      },
      SET_DRAFT (state, { key, text }) { state.drafts = { ...state.drafts, [key]: text || '' } },
      SET_NOTICE (state, { key, text }) { state.opNotices = { ...state.opNotices, [key]: text || '' } },
      OP_START (state, key) {
        state.busy = { ...state.busy, [key]: true }
        state.opErrors = { ...state.opErrors, [key]: '' }
        state.opNotices = { ...state.opNotices, [key]: '' }
      },
      OP_SUCCESS (state, key) {
        state.busy = { ...state.busy, [key]: false }
        state.opErrors = { ...state.opErrors, [key]: '' }
      },
      OP_FAILURE (state, { key, message }) {
        state.busy = { ...state.busy, [key]: false }
        state.opErrors = { ...state.opErrors, [key]: message || '' }
      },
      CLEAR_OP_ERROR (state, key) { state.opErrors = { ...state.opErrors, [key]: '' } },
      SET_WRITES_BLOCKED (state, value) { state.writesBlocked = !!value },
      SET_JIRA_WRITES_BLOCKED (state, value) { state.jiraWritesBlocked = !!value },
      JIRA_SUMMARY_START (state, taskId) {
        const prev = state.jiraSummaries[taskId] || { data: null, fetchedAt: 0 }
        state.jiraSummaries = { ...state.jiraSummaries, [taskId]: { ...prev, loading: true, error: '' } }
      },
      JIRA_SUMMARY_SUCCESS (state, { taskId, jira, fetchedAt }) {
        const prev = state.jiraSummaries[taskId] || {}
        state.jiraSummaries = { ...state.jiraSummaries, [taskId]: { ...prev, loading: false, error: '', data: jira || null, fetchedAt } }
      },
      JIRA_SUMMARY_FAILURE (state, { taskId, message }) {
        const prev = state.jiraSummaries[taskId] || { data: null, fetchedAt: 0 }
        state.jiraSummaries = { ...state.jiraSummaries, [taskId]: { ...prev, loading: false, error: message || JIRA_COPY.readFailure } }
      },
      JIRA_DECISION_SET (state, { questionId, decision }) {
        state.jiraDecisions = { ...state.jiraDecisions, [questionId]: decision }
      },
      JIRA_DECISION_PATCH (state, { questionId, patch }) {
        const prev = state.jiraDecisions[questionId]
        if (!prev) return
        state.jiraDecisions = { ...state.jiraDecisions, [questionId]: { ...prev, ...patch } }
      }
    },
    actions: {
      // reset: first page for the current filters; append: next page via cursor.
      async loadTickets ({ state, commit, dispatch }, { append } = {}) {
        // No append while a first page is loading (its cursor would belong to the old filter).
        if (append && (!state.nextCursor || state.loadingMore || state.loading)) return { ok: false }
        const seq = ++rt.listSeq
        commit('LIST_START', { append: !!append })
        try {
          const data = await deps.api.adminTickets({
            state: state.filterState,
            admin_id: state.filterAdminId,
            cursor: append ? state.nextCursor : null
          })
          if (seq !== rt.listSeq) return { ok: false, stale: true }
          commit('LIST_SUCCESS', { data, append: !!append })
          dispatch('loadJiraStatuses', jiraIdsOf(data && data.tickets))
          return { ok: true }
        } catch (err) {
          if (seq !== rt.listSeq) return { ok: false, stale: true }
          commit('LIST_FAILURE', errorMessage(err, deps.mapError, ADMIN_ERROR_MESSAGES.list))
          return { ok: false }
        }
      },
      loadMore ({ dispatch }) {
        return dispatch('loadTickets', { append: true })
      },
      // Status tag per linked task: one request per 50 ids, results merged as
      // they arrive. Failures leave the keys untouched (no tag, no error).
      async loadJiraStatuses ({ commit }, taskIds) {
        const ids = []
        ;(taskIds || []).forEach(id => { const s = String(id || ''); if (s && ids.indexOf(s) === -1) ids.push(s) })
        if (!ids.length || typeof deps.api.jiraStatuses !== 'function') return { ok: true }
        let ok = true
        for (let i = 0; i < ids.length; i += JIRA_STATUSES_BATCH) {
          try {
            const map = await deps.api.jiraStatuses(ids.slice(i, i + JIRA_STATUSES_BATCH))
            commit('JIRA_STATUSES_MERGE', map)
          } catch (err) {
            ok = false
          }
        }
        return { ok }
      },
      setFilters ({ commit, dispatch }, filters) {
        commit('SET_FILTERS', filters || {})
        return dispatch('loadTickets')
      },
      // Manual "רענון": reload the first page and the open thread (if any).
      refresh ({ state, dispatch }) {
        const jobs = [dispatch('loadTickets')]
        if (state.thread.rootId) jobs.push(dispatch('openThread', state.thread.rootId))
        return Promise.all(jobs)
      },
      async openThread ({ commit }, rootId) {
        const id = parseInt(rootId, 10)
        if (!(id > 0)) return { ok: false }
        const seq = ++rt.threadSeq
        commit('THREAD_START', id)
        try {
          const data = await deps.api.adminThread(id)
          if (seq !== rt.threadSeq) return { ok: false, stale: true }
          commit('THREAD_SUCCESS', data)
          return { ok: true }
        } catch (err) {
          if (seq !== rt.threadSeq) return { ok: false, stale: true }
          commit('THREAD_FAILURE', errorMessage(err, deps.mapError, ADMIN_ERROR_MESSAGES.thread))
          return { ok: false }
        }
      },
      closeThread ({ commit }) {
        rt.threadSeq++
        commit('THREAD_CLOSE')
      },

      // Typing in any supervisor field: keep the draft, clear its stale error.
      setDraft ({ state, commit }, { key, text }) {
        commit('SET_DRAFT', { key, text })
        if (text && state.opNotices[key]) commit('SET_NOTICE', { key, text: '' })
      },

      // "שלח הנחיה ועדכן תשובה": private instruction (≤4000) for the latest
      // genuine recruiter question; the server queues a re-answer job. The
      // target id is part of the payload, so a changed target gets a new uuid.
      sendInstruction (ctx, { text, expectedQuestionId } = {}) {
        const rootId = ctx.state.thread.rootId
        if (!rootId) return Promise.resolve({ ok: false })
        const key = opKey(rootId, 'instruction')
        const target = instructionTarget(ctx.state.thread)
        const expected = parseInt(expectedQuestionId === undefined ? (target && target.id) : expectedQuestionId, 10) || null
        if (!expected) return Promise.resolve({ ok: false })
        const body = String(text === undefined ? (ctx.state.drafts[key] || '') : text)
        const payload = { text: body, expected_question_id: expected }
        return runOp(ctx, {
          rootId,
          key,
          payload,
          maxChars: qaApi.INSTRUCTION_MAX,
          draftKey: key,
          notice: INSTRUCTION_ACCEPTED_TEXT,
          call: uuid => deps.api.adminInstruction(rootId, { request_uuid: uuid, ...payload })
        })
      },

      // "אשר תשובה בשם המערכת" on needs_human. text undefined/null = the reviewed
      // draft exactly; a string = approve-with-text (details/sources dropped).
      approve (ctx, { id, text } = {}) {
        const rootId = ctx.state.thread.rootId
        const q = questionOf(ctx.state, id)
        if (!rootId || !q) return Promise.resolve({ ok: false })
        const key = opKey(rootId, 'approve', id)
        const payload = { expected_revision: q.turn_revision || 0 }
        if (typeof text === 'string') payload.text = text
        return runOp(ctx, {
          rootId,
          key,
          payload,
          maxChars: qaApi.ANSWER_MAX,
          draftKey: key,
          call: uuid => deps.api.adminApprove(id, { request_uuid: uuid, ...payload })
        })
      },

      // "ענה בשם המערכת" -> "שליחת תשובה" (≤350).
      answer (ctx, { id, text } = {}) {
        const rootId = ctx.state.thread.rootId
        const q = questionOf(ctx.state, id)
        if (!rootId || !q) return Promise.resolve({ ok: false })
        const key = opKey(rootId, 'answer', id)
        const body = String(text === undefined ? (ctx.state.drafts[key] || '') : text)
        const payload = { expected_revision: q.turn_revision || 0, text: body }
        return runOp(ctx, {
          rootId,
          key,
          payload,
          maxChars: qaApi.ANSWER_MAX,
          draftKey: key,
          call: uuid => deps.api.adminAnswer(id, { request_uuid: uuid, ...payload })
        })
      },

      // "ביטול המענה" on queued/running/consulting.
      cancel (ctx, { id } = {}) {
        const rootId = ctx.state.thread.rootId
        const q = questionOf(ctx.state, id)
        if (!rootId || !q) return Promise.resolve({ ok: false })
        const key = opKey(rootId, 'cancel', id)
        const payload = { expected_revision: q.turn_revision || 0 }
        return runOp(ctx, {
          rootId,
          key,
          payload,
          call: uuid => deps.api.adminCancel(id, { request_uuid: uuid, ...payload })
        })
      },

      // "סגירת פנייה" / "פתיחה מחדש" with the root's current preconditions.
      setLifecycle (ctx, { close } = {}) {
        const rootId = ctx.state.thread.rootId
        const ticket = ctx.state.thread.ticket || {}
        if (!rootId) return Promise.resolve({ ok: false })
        const key = opKey(rootId, 'lifecycle')
        const payload = {
          action: close ? 'close' : 'reopen',
          expected_notification_version: ticket.notification_version || 0,
          expected_closed: !!ticket.closed_at
        }
        const call = close ? deps.api.adminClose : deps.api.adminReopen
        return runOp(ctx, {
          rootId,
          key,
          payload,
          call: uuid => call(rootId, {
            request_uuid: uuid,
            expected_notification_version: payload.expected_notification_version,
            expected_closed: payload.expected_closed
          })
        })
      },

      // Jira card read. Thread rendering never waits on this. Same task id ->
      // one request; fresh (<30s) summaries are served from the store.
      loadJiraSummary ({ state, commit }, { questionId, taskId, force } = {}) {
        const tid = String(taskId === undefined || taskId === null ? '' : taskId)
        if (!tid || !questionId) return Promise.resolve({ ok: false })
        const entry = state.jiraSummaries[tid]
        if (!force && entry && entry.data && !entry.error && deps.now() - entry.fetchedAt < JIRA_SUMMARY_TTL_MS) {
          return Promise.resolve({ ok: true, cached: true })
        }
        if (!force && rt.jiraInflight[tid]) return rt.jiraInflight[tid]
        const seq = (rt.jiraSeq[tid] || 0) + 1
        rt.jiraSeq[tid] = seq
        commit('JIRA_SUMMARY_START', tid)
        const promise = deps.api.jiraSummary(questionId).then(data => {
          if (rt.jiraSeq[tid] !== seq) return { ok: false, stale: true }
          commit('JIRA_SUMMARY_SUCCESS', { taskId: tid, jira: data && data.jira, fetchedAt: deps.now() })
          return { ok: true }
        }, () => {
          if (rt.jiraSeq[tid] !== seq) return { ok: false, stale: true }
          commit('JIRA_SUMMARY_FAILURE', { taskId: tid, message: JIRA_COPY.readFailure })
          return { ok: false }
        }).then(res => {
          if (rt.jiraInflight[tid] === promise) delete rt.jiraInflight[tid]
          return res
        })
        rt.jiraInflight[tid] = promise
        return promise
      },

      // approve | reject | edit on the summary currently shown. Preconditions
      // come from what the supervisor reviewed; no optimistic success.
      async submitJiraDecision (ctx, { questionId, action, text } = {}) {
        const { state, getters, commit } = ctx
        const q = questionOf(state, questionId)
        if (!q || !q.jira_task_id || qaApi.JIRA_DECISION_ACTIONS.indexOf(action) === -1) return { ok: false }
        if (!getters.canJiraWrite) return { ok: false }
        if (isJiraDecisionBusy(state.jiraDecisions[questionId])) return { ok: false, busy: true }
        const taskId = String(q.jira_task_id)
        const summary = (state.jiraSummaries[taskId] || {}).data
        if (!summary || (summary.approval && Array.isArray(summary.approval.allowed_actions) && summary.approval.allowed_actions.indexOf(action) === -1)) {
          return { ok: false }
        }
        const body = String(text || '').trim()
        if ((action === 'reject' || action === 'edit') && !body) return { ok: false, message: qaApi.JIRA_MESSAGES.text_invalid }
        if (body.length > qaApi.JIRA_TEXT_MAX) return { ok: false, message: qaApi.JIRA_MESSAGES.text_too_long }
        const payload = {
          action,
          text: body,
          expected_revision: q.turn_revision || 0,
          expected_jira_task_id: taskId,
          draft_version: summary.draft ? summary.draft.draft_version : 0,
          request_version: summary.request_version,
          approval_revision: summary.approval_revision
        }
        const uuid = jiraUuidFor(questionId, payload)
        stopPoll(questionId)
        commit('JIRA_DECISION_SET', {
          questionId,
          decision: { state: 'submitting', action, uuid, rootId: state.thread.rootId, taskId, message: '', stale: false, pollExpired: false }
        })
        let res
        try {
          res = await deps.api.jiraDecision(questionId, { request_uuid: uuid, ...payload })
        } catch (e) {
          return failJiraDecision(ctx, questionId, deps.mapJiraError(e))
        }
        return applyJiraResult(ctx, questionId, res)
      },

      // Status check for a pending decision (timer or "בדוק שוב" with manual: true).
      // An unconfirmed decision is re-checked only manually; a fresh 60s window then applies.
      async pollJiraDecision (ctx, { questionId, manual } = {}) {
        const dec = ctx.state.jiraDecisions[questionId]
        const waiting = d => !!d && (d.state === 'pending' || d.state === 'unconfirmed')
        if (!waiting(dec) || !dec.uuid || (dec.state === 'unconfirmed' && !manual)) return { ok: false }
        if (manual) {
          stopPoll(questionId)
          ctx.commit('JIRA_DECISION_PATCH', { questionId, patch: { pollExpired: false, checking: true } })
        }
        const uuid = dec.uuid
        const current = () => {
          const cur = ctx.state.jiraDecisions[questionId]
          if (manual) ctx.commit('JIRA_DECISION_PATCH', { questionId, patch: { checking: false } })
          return waiting(cur) && cur.uuid === uuid
        }
        let res
        try {
          res = await deps.api.jiraDecisionStatus(questionId, uuid)
        } catch (e) {
          if (!current()) return { ok: false, stale: true }
          return failJiraDecision(ctx, questionId, deps.mapJiraError(e), { fromPoll: true })
        }
        if (!current()) return { ok: false, stale: true }
        return applyJiraResult(ctx, questionId, res)
      },

      // Card (re)mounted: a pending decision whose poll loop is not running resumes polling.
      // After a page reload the decision is restored from sessionStorage (same uuid).
      resumeJiraDecision (ctx, { questionId } = {}) {
        let dec = ctx.state.jiraDecisions[questionId]
        if (!dec) {
          const saved = readPersisted()[questionId]
          if (!saved || !saved.uuid) return
          if (saved.digest) rt.jiraOps[questionId] = { uuid: saved.uuid, digest: saved.digest, renew: false }
          dec = { state: 'pending', action: saved.action, uuid: saved.uuid, rootId: saved.rootId, taskId: saved.taskId, message: '', stale: false, pollExpired: false }
          ctx.commit('JIRA_DECISION_SET', { questionId, decision: dec })
        }
        if (dec.state !== 'pending' || dec.pollExpired) return
        const poll = rt.jiraPolls[questionId]
        if (poll && poll.timer) return
        stopPoll(questionId)
        schedulePoll(ctx, questionId, 0)
      },

      // Clear a finished card message (e.g. the user starts a new decision).
      clearJiraDecision ({ state, commit }, { questionId } = {}) {
        const dec = state.jiraDecisions[questionId]
        if (!dec || isJiraDecisionBusy(dec)) return
        commit('JIRA_DECISION_PATCH', { questionId, patch: { state: 'idle', message: '', stale: false } })
      }
    }
  }
}

export default createRecruiterQaAdminModule()
